import { OptionType } from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { useGetCustomerFormOptions } from '@/customers/application/adapters/getCustomerFormOptions';
import { filterAutocompleteOptions } from '@/customers/application/utils/autocompleteUtils';

/**
 * Hook that transforms customer form options into OptionType format for autocomplete components
 * Returns transformed options and a generic filter function
 *
 * @param skip - Whether to skip fetching the form options
 * @returns Object containing transformed options and loading state
 */
export const useCustomerFormOptionsTransformed = (skip: boolean) => {
  const {
    availablePricingSchemes,
    availableTerms,
    availableSalesRegions,
    availableSalesAgents,
    loading: formOptionsLoading
  } = useGetCustomerFormOptions(skip);

  // Transform pricing schemes to OptionType format
  const pricingSchemeOptions: OptionType[] = (availablePricingSchemes ?? []).map((scheme) => ({
    value: scheme.id ?? -1,
    label: (scheme as { schemeName?: string }).schemeName ?? 'Unknown Pricing Scheme',
    key: `pricing-scheme-${scheme.id}`
  }));

  // Transform terms to OptionType format
  const termOptions: OptionType[] = (availableTerms ?? []).map((term) => ({
    value: term.id ?? -1,
    label: (term as { termName?: string }).termName ?? 'Unknown Term',
    key: `term-${term.id}`
  }));

  // Transform sales regions to OptionType format
  const salesRegionOptions: OptionType[] = (availableSalesRegions ?? []).map((region) => ({
    value: region.id ?? -1,
    label: (region as { salesRegionName?: string }).salesRegionName ?? 'Unknown Sales Region',
    key: `sales-region-${region.id}`
  }));

  // Transform sales agents to OptionType format
  const salesAgentOptions: OptionType[] = (availableSalesAgents ?? []).map((agent) => ({
    value: agent.empId ?? -1,
    label: agent.fullName ?? 'Unknown Sales Agent',
    key: `sales-agent-${agent.empId}`
  }));

  const defaultSalesAgentOption = (availableSalesAgents ?? []).find((agent) => {
    const withDefault = agent as { default?: boolean | null };
    return Boolean(withDefault.default) && typeof agent.empId === 'number';
  });

  return {
    pricingSchemeOptions,
    termOptions,
    salesRegionOptions,
    salesAgentOptions,
    defaultSalesAgentOption: defaultSalesAgentOption
      ? {
          value: defaultSalesAgentOption.empId,
          label: defaultSalesAgentOption.fullName ?? 'Unknown Sales Agent',
          key: `sales-agent-${defaultSalesAgentOption.empId}`
        }
      : undefined,
    loading: formOptionsLoading,
    filterOptions: filterAutocompleteOptions
  };
};
